/**
 * Session Loading Debug System
 *
 * This module provides comprehensive debugging capabilities for session loading,
 * allowing developers and support team to diagnose session loading issues quickly.
 *
 * Features:
 * - Configurable debug levels (NONE, BASIC, DETAILED, VERBOSE)
 * - Step-by-step session loading tracking
 * - Performance monitoring with timestamps
 * - Error capture and categorization
 * - Export logs for customer support
 * - Easy toggle via environment variables or localStorage
 */

// Debug levels
export const DEBUG_LEVELS = {
  NONE: 0,
  BASIC: 1,
  DETAILED: 2,
  VERBOSE: 3,
};

// Debug categories for filtering
export const DEBUG_CATEGORIES = {
  SESSION_LOAD: 'SESSION_LOAD',
  REDUX: 'REDUX',
  APOLLO: 'APOLLO',
  COMPONENT: 'COMPONENT',
  NETWORK: 'NETWORK',
  PERFORMANCE: 'PERFORMANCE',
  ERROR: 'ERROR',
};

// Session loading steps for tracking progress
export const SESSION_LOAD_STEPS = {
  INIT: 'INIT',
  PREFETCH_START: 'PREFETCH_START',
  PREFETCH_SUCCESS: 'PREFETCH_SUCCESS',
  PREFETCH_ERROR: 'PREFETCH_ERROR',
  REDUX_LOAD_START: 'REDUX_LOAD_START',
  REDUX_LOAD_SUCCESS: 'REDUX_LOAD_SUCCESS',
  REDUX_LOAD_ERROR: 'REDUX_LOAD_ERROR',
  PRODUCER_INIT_START: 'PRODUCER_INIT_START',
  PRODUCER_INIT_SUCCESS: 'PRODUCER_INIT_SUCCESS',
  PRODUCER_INIT_ERROR: 'PRODUCER_INIT_ERROR',
  READY_TO_RENDER: 'READY_TO_RENDER',
  COMPONENT_MOUNT: 'COMPONENT_MOUNT',
  SUBSCRIPTIONS_START: 'SUBSCRIPTIONS_START',
  SUBSCRIPTIONS_SUCCESS: 'SUBSCRIPTIONS_SUCCESS',
};

class SessionDebugger {
  constructor() {
    this.logs = [];
    this.currentSessionKey = null;
    this.sessionStartTime = null;
    this.stepTimestamps = {};
    this.errors = [];
    this.performance = {};

    // Check for debug configuration
    this.debugLevel = this.getDebugLevel();
    this.isEnabled = this.debugLevel > DEBUG_LEVELS.NONE;

    if (this.isEnabled) {
      this.log(
        'SESSION_DEBUG',
        'Session debugging enabled',
        {
          level: this.debugLevel,
          levelName: Object.keys(DEBUG_LEVELS)[this.debugLevel],
        },
        DEBUG_CATEGORIES.SESSION_LOAD
      );
    }
  }

  getDebugLevel() {
    // Check environment variable first
    if (process.env.REACT_APP_SESSION_DEBUG_LEVEL) {
      const envLevel = parseInt(process.env.REACT_APP_SESSION_DEBUG_LEVEL);
      if (!isNaN(envLevel) && envLevel >= 0 && envLevel <= 3) {
        return envLevel;
      }
    }

    // Check localStorage
    const localStorageLevel = localStorage.getItem(
      'virtius-session-debug-level'
    );
    if (localStorageLevel) {
      const level = parseInt(localStorageLevel);
      if (!isNaN(level) && level >= 0 && level <= 3) {
        return level;
      }
    }

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlLevel = urlParams.get('sessionDebug');
    if (urlLevel) {
      const level = parseInt(urlLevel);
      if (!isNaN(level) && level >= 0 && level <= 3) {
        return level;
      }
    }

    return DEBUG_LEVELS.NONE;
  }

  startSession(sessionKey) {
    if (!this.isEnabled) return;

    this.currentSessionKey = sessionKey;
    this.sessionStartTime = Date.now();
    this.stepTimestamps = {};
    this.errors = [];
    this.performance = {};

    this.log(
      'SESSION_START',
      'Starting session load',
      {
        sessionKey,
        timestamp: this.sessionStartTime,
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
      DEBUG_CATEGORIES.SESSION_LOAD
    );

    this.trackStep(SESSION_LOAD_STEPS.INIT);
  }

  trackStep(step, data = {}) {
    if (!this.isEnabled) return;

    const now = Date.now();
    const elapsed = this.sessionStartTime ? now - this.sessionStartTime : 0;

    this.stepTimestamps[step] = {
      timestamp: now,
      elapsed,
      ...data,
    };

    if (this.debugLevel >= DEBUG_LEVELS.BASIC) {
      this.log(
        'STEP_TRACKED',
        `Session load step: ${step}`,
        {
          step,
          elapsed: `${elapsed}ms`,
          ...data,
        },
        DEBUG_CATEGORIES.SESSION_LOAD
      );
    }
  }

  trackReduxAction(action, state, previousState) {
    if (!this.isEnabled || this.debugLevel < DEBUG_LEVELS.DETAILED) return;

    const relevantActions = [
      'LOAD_SESSION_REQUEST',
      'LOAD_SESSION_SUCCESS',
      'LOAD_SESSION_FAILURE',
      'INITIALIZE',
      'CHANGE_VIEW',
    ];

    if (relevantActions.some((a) => action.type.includes(a))) {
      this.log(
        'REDUX_ACTION',
        `Redux action dispatched: ${action.type}`,
        {
          action: action.type,
          payload: action,
          sessionState: {
            loadingState: state.session?.loadingState,
            sessionKey: state.session?.sessionKey,
            sessionId: state.session?.id,
            isLoaded: !!state.session?.id,
          },
          producerState: {
            loaded: state.producer?.loaded,
            teamA: !!state.producer?.teamA,
            teamB: !!state.producer?.teamB,
          },
        },
        DEBUG_CATEGORIES.REDUX
      );
    }
  }

  trackApolloQuery(queryName, variables, result, error) {
    if (!this.isEnabled || this.debugLevel < DEBUG_LEVELS.DETAILED) return;

    const relevantQueries = [
      'PrefetchSession',
      'GetFullSession',
      'SessionByKey',
    ];

    if (relevantQueries.some((q) => queryName.includes(q))) {
      this.log(
        'APOLLO_QUERY',
        `Apollo query: ${queryName}`,
        {
          queryName,
          variables,
          hasData: !!result?.data,
          hasError: !!error,
          error: error?.message,
          networkStatus: result?.networkStatus,
          loading: result?.loading,
        },
        DEBUG_CATEGORIES.APOLLO
      );
    }
  }

  trackComponent(componentName, state, props = {}) {
    if (!this.isEnabled || this.debugLevel < DEBUG_LEVELS.VERBOSE) return;

    const relevantComponents = [
      'Session',
      'FullscreenLoading',
      'Main',
      'Producer',
    ];

    if (relevantComponents.some((c) => componentName.includes(c))) {
      this.log(
        'COMPONENT_STATE',
        `Component render: ${componentName}`,
        {
          componentName,
          state,
          relevantProps: {
            sessionId: props.sessionId,
            loading: props.loading,
            error: props.error,
          },
        },
        DEBUG_CATEGORIES.COMPONENT
      );
    }
  }

  trackError(error, context = '', category = DEBUG_CATEGORIES.ERROR) {
    if (!this.isEnabled) return;

    const errorInfo = {
      message: error?.message || 'Unknown error',
      stack: error?.stack,
      context,
      timestamp: Date.now(),
      sessionKey: this.currentSessionKey,
      category,
    };

    this.errors.push(errorInfo);

    this.log(
      'ERROR',
      `Error occurred: ${context}`,
      errorInfo,
      DEBUG_CATEGORIES.ERROR
    );
  }

  trackPerformance(operation, duration, metadata = {}) {
    if (!this.isEnabled || this.debugLevel < DEBUG_LEVELS.DETAILED) return;

    if (!this.performance[operation]) {
      this.performance[operation] = [];
    }

    this.performance[operation].push({
      duration,
      timestamp: Date.now(),
      ...metadata,
    });

    this.log(
      'PERFORMANCE',
      `Performance: ${operation}`,
      {
        operation,
        duration: `${duration}ms`,
        ...metadata,
      },
      DEBUG_CATEGORIES.PERFORMANCE
    );
  }

  log(event, message, data = {}, category = DEBUG_CATEGORIES.SESSION_LOAD) {
    if (!this.isEnabled) return;

    const logEntry = {
      timestamp: new Date().toISOString(),
      sessionKey: this.currentSessionKey,
      event,
      message,
      category,
      data,
      elapsed: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0,
    };

    this.logs.push(logEntry);

    // Console output based on debug level
    if (this.debugLevel >= DEBUG_LEVELS.BASIC) {
      const prefix = `[VIRTIUS-DEBUG:${category}]`;

      if (category === DEBUG_CATEGORIES.ERROR) {
        console.error(prefix, message, data);
      } else if (this.debugLevel >= DEBUG_LEVELS.VERBOSE) {
        console.log(prefix, message, data);
      } else if (
        this.debugLevel >= DEBUG_LEVELS.DETAILED &&
        [
          DEBUG_CATEGORIES.SESSION_LOAD,
          DEBUG_CATEGORIES.REDUX,
          DEBUG_CATEGORIES.APOLLO,
        ].includes(category)
      ) {
        console.log(prefix, message);
      } else if (category === DEBUG_CATEGORIES.SESSION_LOAD) {
        console.log(prefix, message);
      }
    }
  }

  getDebugReport() {
    if (!this.isEnabled) {
      return { message: 'Debug logging is disabled' };
    }

    const report = {
      sessionKey: this.currentSessionKey,
      sessionStartTime: this.sessionStartTime
        ? new Date(this.sessionStartTime).toISOString()
        : null,
      totalElapsed: this.sessionStartTime
        ? Date.now() - this.sessionStartTime
        : 0,
      debugLevel: this.debugLevel,
      debugLevelName: Object.keys(DEBUG_LEVELS)[this.debugLevel],
      steps: this.stepTimestamps,
      errors: this.errors,
      performance: this.performance,
      logs: this.logs,
      summary: this.generateSummary(),
      generatedAt: new Date().toISOString(),
    };

    return report;
  }

  generateSummary() {
    const summary = {
      totalLogs: this.logs.length,
      errorCount: this.errors.length,
      stepsCompleted: Object.keys(this.stepTimestamps).length,
      categories: {},
    };

    // Count logs by category
    this.logs.forEach((log) => {
      summary.categories[log.category] =
        (summary.categories[log.category] || 0) + 1;
    });

    // Identify potential issues
    const issues = [];

    if (this.errors.length > 0) {
      issues.push(`${this.errors.length} errors occurred`);
    }

    const stepCount = Object.keys(this.stepTimestamps).length;
    if (stepCount < 3) {
      issues.push('Very few loading steps completed - possible early failure');
    }

    if (
      this.stepTimestamps[SESSION_LOAD_STEPS.PREFETCH_SUCCESS] &&
      !this.stepTimestamps[SESSION_LOAD_STEPS.REDUX_LOAD_SUCCESS]
    ) {
      issues.push('GraphQL succeeded but Redux load failed');
    }

    if (
      this.stepTimestamps[SESSION_LOAD_STEPS.REDUX_LOAD_SUCCESS] &&
      !this.stepTimestamps[SESSION_LOAD_STEPS.READY_TO_RENDER]
    ) {
      issues.push('Session loaded but component not ready to render');
    }

    summary.issues = issues;

    return summary;
  }

  exportLogs(format = 'json') {
    const report = this.getDebugReport();

    if (format === 'json') {
      return JSON.stringify(report, null, 2);
    } else if (format === 'text') {
      return this.formatAsText(report);
    }

    return report;
  }

  formatAsText(report) {
    let text = `VIRTIUS SESSION DEBUG REPORT\n`;
    text += `===============================\n\n`;
    text += `Session Key: ${report.sessionKey || 'N/A'}\n`;
    text += `Debug Level: ${report.debugLevelName} (${report.debugLevel})\n`;
    text += `Total Time: ${report.totalElapsed}ms\n`;
    text += `Generated: ${report.generatedAt}\n\n`;

    if (report.summary.issues.length > 0) {
      text += `ISSUES DETECTED:\n`;
      report.summary.issues.forEach((issue) => {
        text += `- ${issue}\n`;
      });
      text += '\n';
    }

    text += `LOADING STEPS:\n`;
    Object.entries(report.steps).forEach(([step, info]) => {
      text += `- ${step}: ${info.elapsed}ms\n`;
    });
    text += '\n';

    if (report.errors.length > 0) {
      text += `ERRORS:\n`;
      report.errors.forEach((error, i) => {
        text += `${i + 1}. ${error.context}: ${error.message}\n`;
      });
      text += '\n';
    }

    text += `LOG SUMMARY:\n`;
    text += `- Total entries: ${report.summary.totalLogs}\n`;
    Object.entries(report.summary.categories).forEach(([category, count]) => {
      text += `- ${category}: ${count}\n`;
    });

    return text;
  }

  downloadReport(filename, format = 'json') {
    const content = this.exportLogs(format);
    const blob = new Blob([content], {
      type: format === 'json' ? 'application/json' : 'text/plain',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename ||
      `session-debug-${
        this.currentSessionKey || 'unknown'
      }-${Date.now()}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Set the debug level dynamically
   */
  setDebugLevel(level) {
    if (level >= DEBUG_LEVELS.NONE && level <= DEBUG_LEVELS.VERBOSE) {
      this.debugLevel = level;
      this.isEnabled = this.debugLevel > DEBUG_LEVELS.NONE;

      // Store in localStorage for persistence
      localStorage.setItem('virtius-session-debug-level', level.toString());

      if (this.isEnabled) {
        this.log(
          'DEBUG_LEVEL_CHANGED',
          `Debug level changed to ${
            Object.keys(DEBUG_LEVELS)[level]
          } (${level})`,
          { level, levelName: Object.keys(DEBUG_LEVELS)[level] },
          DEBUG_CATEGORIES.SESSION_LOAD
        );
      }
    }
  }

  /**
   * Enable debugging
   */
  enable() {
    if (this.debugLevel === DEBUG_LEVELS.NONE) {
      this.setDebugLevel(DEBUG_LEVELS.BASIC);
    } else {
      this.isEnabled = true;
    }
  }

  /**
   * Disable debugging
   */
  disable() {
    this.isEnabled = false;
    // Don't change debugLevel, just disable
    localStorage.removeItem('virtius-session-debug-level');
  }

  clear() {
    this.logs = [];
    this.errors = [];
    this.stepTimestamps = {};
    this.performance = {};
    this.currentSessionKey = null;
    this.sessionStartTime = null;

    if (this.isEnabled) {
      this.log('DEBUG_CLEARED', 'Debug logs cleared');
    }
  }
}

// Create singleton instance
export const sessionDebugger = new SessionDebugger();

// Helper functions for easy access
export const debugLog = (event, message, data, category) =>
  sessionDebugger.log(event, message, data, category);

export const debugStep = (step, data) => sessionDebugger.trackStep(step, data);

export const debugError = (error, context, category) =>
  sessionDebugger.trackError(error, context, category);

export const debugPerformance = (operation, duration, metadata) =>
  sessionDebugger.trackPerformance(operation, duration, metadata);

// Expose to window for manual debugging
if (typeof window !== 'undefined') {
  window.sessionDebugger = sessionDebugger;
  window.downloadSessionDebugReport = (format = 'json') => {
    sessionDebugger.downloadReport(null, format);
  };
}

export default sessionDebugger;
